class SousaPackBuilder extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;

    this.strings = this.parseJson(this.querySelector('[data-pack-strings]')) || {};
    this.cards = Array.from(this.querySelectorAll('[data-pack-target]'));
    this.groups = Array.from(this.querySelectorAll('[data-pack-panel]'));
    this.panels = Array.from(this.querySelectorAll('[data-pack-body]')).map((el) => this.buildPanel(el));

    this.cards.forEach((card) => {
      card.addEventListener('click', () => this.select(card.dataset.packTarget));
    });

    this.querySelectorAll('[data-pack-media]').forEach((media) => this.bindMedia(media));
    this.bindTabs();

    const active = this.cards.find((card) => card.classList.contains('is-active'));
    this.select(active ? active.dataset.packTarget : this.dataset.activePack);
  }

  buildPanel(el) {
    const panel = {
      index: el.dataset.packBody,
      root: el,
      data: this.parseJson(el.querySelector('[data-pack-data]')) || {},
      form: el.querySelector('[data-pack-form]'),
      variantInput: el.querySelector('[data-pack-variant-input]'),
      qtyInput: el.querySelector('[data-pack-qty-input]'),
      priceSink: el.querySelector('[data-pack-skio-price]'),
      picker: el.querySelector('skio-plan-picker'),
      button: el.querySelector('[data-pack-submit]'),
      atcContent: el.querySelector('[data-pack-atc-content]'),
      atcLabel: el.querySelector('[data-pack-atc-label]'),
      atcPrice: el.querySelector('[data-pack-atc-price]'),
      atcSeparator: el.querySelector('[data-pack-atc-separator]'),
      atcSubtitle: el.querySelector('[data-pack-atc-subtitle]'),
      errorEl: el.querySelector('[data-pack-error]'),
      flavourInputs: Array.from(el.querySelectorAll('[data-pack-flavour]')),
      priceText: '',
    };

    panel.flavourInputs.forEach((input) => {
      input.addEventListener('change', () => this.onFlavourChange(panel));
    });

    el.querySelectorAll('[data-pack-qty-step]').forEach((button) => {
      button.addEventListener('click', () => {
        this.stepQuantity(panel, Number(button.dataset.packQtyStep));
      });
    });

    if (panel.qtyInput) {
      panel.qtyInput.addEventListener('input', () => this.syncQuantities());
      panel.qtyInput.addEventListener('change', () => this.stepQuantity(panel, 0));
    }

    if (panel.form) {
      panel.form.addEventListener('submit', (event) => this.submit(panel, event));
    }

    el.addEventListener('skio::update-selling-plan', () => {
      requestAnimationFrame(() => this.refresh(panel));
    });

    if (panel.priceSink) {
      panel.priceObserver = new MutationObserver(() => {
        requestAnimationFrame(() => this.refresh(panel));
      });
      panel.priceObserver.observe(panel.priceSink, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }

    return panel;
  }

  bindTabs() {
    this.tabs = Array.from(this.querySelectorAll('[data-pack-tab]'));
    this.tabPanels = Array.from(this.querySelectorAll('[data-pack-tabpanel]'));
    if (!this.tabs.length) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.selectTab(tab.dataset.packTab));
      tab.addEventListener('keydown', (event) => {
        const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (!step) return;
        event.preventDefault();
        const index = this.tabs.indexOf(tab);
        const next = this.tabs[(index + step + this.tabs.length) % this.tabs.length];
        this.selectTab(next.dataset.packTab);
        next.focus();
      });
    });
  }

  selectTab(key) {
    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.packTab === key;
      tab.classList.toggle('tab-title--active', isActive);
      tab.classList.toggle('after:bg-scheme-text', isActive);
      tab.classList.toggle('after:bg-transparent', !isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });
    this.tabPanels.forEach((panel) => {
      panel.hidden = panel.dataset.packTabpanel !== key;
    });
  }

  bindMedia(media) {
    media.querySelectorAll('[data-pack-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        this.showMedia(media, thumb.dataset.packThumb);
      });
    });

    const track = media.querySelector('[data-pack-track]');
    if (!track) return;

    media.querySelectorAll('[data-pack-arrow]').forEach((arrow) => {
      arrow.addEventListener('click', () => {
        this.stepCarousel(media, Number(arrow.dataset.packArrow));
      });
    });

    track.addEventListener('scroll', () => {
      if (media.scrollFrame) return;
      media.scrollFrame = requestAnimationFrame(() => {
        media.scrollFrame = null;
        this.syncCarousel(media);
      });
    });

    this.syncCarousel(media);
  }

  isCarousel(track) {
    return track.scrollWidth - track.clientWidth > 1;
  }

  slideAt(media, index) {
    const slides = media.querySelectorAll('[data-pack-slide]');
    return slides[Math.max(0, Math.min(slides.length - 1, index))] || null;
  }

  currentSlideIndex(media) {
    const track = media.querySelector('[data-pack-track]');
    const slides = Array.from(media.querySelectorAll('[data-pack-slide]'));
    if (!track || !slides.length) return 0;
    const bounds = track.getBoundingClientRect();
    const middle = bounds.left + bounds.width / 2;
    let closest = 0;
    let best = Infinity;
    slides.forEach((slide, i) => {
      const rect = slide.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - middle);
      if (distance < best) {
        best = distance;
        closest = i;
      }
    });
    return closest;
  }

  stepCarousel(media, step) {
    const target = this.currentSlideIndex(media) + step;
    const slide = this.slideAt(media, target);
    if (!slide) return;
    this.scrollToSlide(media, slide, true);
    this.syncCarousel(media, target);
  }

  prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  scrollToSlide(media, slide, smooth) {
    const track = media.querySelector('[data-pack-track]');
    if (!track) return;
    const delta = slide.getBoundingClientRect().left - track.getBoundingClientRect().left;
    if (Math.abs(delta) < 1) return;
    const behavior = smooth && !this.prefersReducedMotion() ? 'smooth' : 'instant';
    track.scrollBy({ left: delta, behavior: behavior });
  }

  syncCarousel(media, indexOverride) {
    const track = media.querySelector('[data-pack-track]');
    if (!track) return;
    const scrollable = this.isCarousel(track);
    const count = media.querySelectorAll('[data-pack-slide]').length;
    const index = indexOverride == null ? this.currentSlideIndex(media) : indexOverride;
    media.querySelectorAll('[data-pack-arrow]').forEach((arrow) => {
      const step = Number(arrow.dataset.packArrow);
      const next = index + step;
      arrow.disabled = !scrollable || next < 0 || next > count - 1;
    });
  }

  parseJson(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  }

  select(index) {
    if (!index) return;
    this.active = index;

    this.groups.forEach((el) => {
      el.classList.toggle('is-active', el.dataset.packPanel === index);
    });

    this.cards.forEach((card) => {
      const isActive = card.dataset.packTarget === index;
      card.classList.toggle('is-active', isActive);
      card.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    this.syncQuantities();

    const media = this.querySelector(`[data-pack-media="${index}"]`);
    if (media) this.syncCarousel(media);

    const panel = this.panels.find((item) => item.index === index);
    if (panel) this.refresh(panel);
  }

  selectedVariant(panel) {
    const variants = panel.data.variants || [];
    if (!variants.length) return null;
    const checked = panel.flavourInputs.find((input) => input.checked);
    if (checked) {
      const match = variants.find((variant) => String(variant.id) === checked.value);
      if (match) return match;
    }
    return variants.find((variant) => variant.available) || variants[0];
  }

  sellingPlanInput(panel) {
    if (!panel.form || !panel.form.elements) return null;
    let input = panel.form.elements.namedItem('selling_plan');
    if (input && typeof input.item === 'function') input = input.item(0);
    return input || null;
  }

  isSubscribed(panel) {
    const input = this.sellingPlanInput(panel);
    return Boolean(input && input.value);
  }

  onFlavourChange(panel) {
    const variant = this.selectedVariant(panel);
    if (!variant) return;

    if (panel.variantInput) {
      panel.variantInput.value = variant.id;
      panel.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const media = this.querySelector(`[data-pack-media="${panel.index}"]`);
    if (media && variant.media_id) this.showMedia(media, String(variant.media_id));

    this.refresh(panel);
  }

  showMedia(media, mediaId) {
    let target = null;
    media.querySelectorAll('[data-pack-slide]').forEach((slide) => {
      const isActive = slide.dataset.packSlide === mediaId;
      slide.classList.toggle('is-active', isActive);
      if (isActive) target = slide;
    });
    media.querySelectorAll('[data-pack-thumb]').forEach((thumb) => {
      thumb.classList.toggle('is-active', thumb.dataset.packThumb === mediaId);
    });

    const track = media.querySelector('[data-pack-track]');
    if (target && track && this.isCarousel(track)) this.scrollToSlide(media, target);
    this.syncCarousel(media);
  }

  stepQuantity(panel, step) {
    if (!panel.qtyInput) return;
    const current = parseInt(panel.qtyInput.value, 10);
    const next = Math.max(1, (Number.isFinite(current) ? current : 1) + step);
    if (String(panel.qtyInput.value) !== String(next)) {
      panel.qtyInput.value = next;
      panel.qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
    this.syncQuantities();
  }

  panelQuantity(panel) {
    if (!panel.qtyInput) return 1;
    const value = parseInt(panel.qtyInput.value, 10);
    return Math.max(1, Number.isFinite(value) ? value : 1);
  }

  syncQuantities() {
    this.panels.forEach((panel) => {
      if (!panel.picker) return;
      const quantity = this.panelQuantity(panel);
      if (panel.picker.quantity !== quantity) panel.picker.quantity = quantity;
    });
  }

  refresh(panel) {
    const variant = this.selectedVariant(panel);
    const subscribed = this.isSubscribed(panel);
    const available = Boolean(variant && variant.available);

    const skioText = panel.priceSink ? panel.priceSink.textContent.trim() : '';
    panel.priceText = skioText || (variant ? variant.money : '');

    if (panel.atcLabel) {
      let label = this.strings.addToCart;
      if (!available) {
        label = this.strings.soldOut;
      } else if (subscribed) {
        label = this.strings.subscribe;
      }
      panel.atcLabel.textContent = label || '';
    }

    if (panel.atcPrice) {
      panel.atcPrice.textContent = available ? panel.priceText : '';
    }

    if (panel.atcSeparator) {
      panel.atcSeparator.textContent = available && panel.priceText ? ' · ' : '';
    }

    if (panel.button) {
      panel.button.disabled = !available;
    }

    if (panel.atcSubtitle) {
      panel.atcSubtitle.textContent = available ? this.summary(panel, variant, subscribed) : '';
    }

    if (panel.atcContent && panel.data.hasFlavours) {
      const handle = variant && variant.handle ? variant.handle : '';
      if (panel.atcContent.dataset.flavour !== handle) panel.atcContent.dataset.flavour = handle;
    }

    if (panel.index === this.active) {
      this.refreshCards(panel, variant, subscribed);
    }
  }

  summary(panel, variant, subscribed) {
    const parts = [];
    const flavour = this.flavour(panel, variant);
    if (flavour) parts.push(flavour);
    if (panel.data.count) parts.push(`${panel.data.count}${this.strings.countSuffix || ''}`);
    parts.push(subscribed ? this.strings.subscription : this.strings.oneTime);
    return parts.filter(Boolean).join(' · ');
  }

  flavour(panel, variant) {
    if (panel.data.hasFlavours && variant && variant.title) return variant.title;
    return this.strings.assorted;
  }

  refreshCards(panel, variant, subscribed) {
    this.cards.forEach((card) => {
      const priceEl = card.querySelector('[data-pack-card-price]');
      if (!priceEl) return;

      let money = priceEl.dataset.onetimeMoney;
      if (subscribed) {
        if (card.dataset.packTarget === panel.index && panel.priceText) {
          money = panel.priceText;
        } else if (priceEl.dataset.subscriptionMoney) {
          money = priceEl.dataset.subscriptionMoney;
        }
      }
      this.renderMoney(priceEl, money);

      if (card.dataset.packTarget === panel.index && panel.data.hasFlavours && variant) {
        card.dataset.flavour = variant.handle || '';
      } else if (card.dataset.packTarget === panel.index) {
        delete card.dataset.flavour;
      }
    });
  }

  renderMoney(el, money) {
    const value = String(money == null ? '' : money);
    el.textContent = '';
    const match = value.match(/^(.*?)(\.\d+)(\D*)$/);
    if (!match) {
      el.textContent = value;
      return;
    }
    const whole = document.createElement('span');
    whole.className = 'pack-builder__card-price-whole';
    whole.textContent = match[1];
    const cents = document.createElement('span');
    cents.className = 'pack-builder__card-price-cents';
    cents.textContent = match[2] + match[3];
    el.append(whole, cents);
  }

  submit(panel, event) {
    event.preventDefault();
    if (!panel.form || panel.loading) return;

    panel.loading = true;
    if (panel.button) panel.button.classList.add('is-loading');
    if (panel.errorEl) panel.errorEl.textContent = '';

    const routes = (window.theme && window.theme.routes) || {};
    const cartType = window.theme && window.theme.settings ? window.theme.settings.cartType : null;
    const modalCart = cartType === 'modal';
    const formData = new FormData(panel.form);

    if (modalCart) {
      formData.append('sections', 'cart-items,cart-footer,cart-item-count');
      formData.append('sections_url', window.location.pathname);
    }

    fetch(routes.cart_add_url || '/cart/add', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/javascript',
      },
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        panel.loading = false;
        if (panel.button) panel.button.classList.remove('is-loading');

        if (data.status) {
          const message = data.description || data.message || this.strings.error;
          if (panel.errorEl) panel.errorEl.textContent = message;
          document.body.dispatchEvent(
            new CustomEvent('shapes:cart:adderror', {
              bubbles: true,
              detail: {
                source: 'product-form',
                sourceId: panel.form.getAttribute('id'),
                variantId: Number(formData.get('id')),
                errors: data.errors || data.description,
                message,
              },
            })
          );
          return;
        }

        if (modalCart) {
          document.body.dispatchEvent(
            new CustomEvent('shapes:cart:afteradditem', {
              bubbles: true,
              detail: { response: data, sourceId: panel.form.getAttribute('id') },
            })
          );
          return;
        }

        window.location.href = routes.cart_url || '/cart';
      })
      .catch(() => {
        panel.loading = false;
        if (panel.button) panel.button.classList.remove('is-loading');
        if (panel.errorEl) panel.errorEl.textContent = this.strings.error || '';
      });
  }
}

if (!customElements.get('sousa-pack-builder')) {
  customElements.define('sousa-pack-builder', SousaPackBuilder);
}
